import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { drivingLogVehiclesService, drivingLogEntriesService } from '../services/drivingLog'
import type { Database } from '../lib/supabase'

type VehicleInsert = Database['public']['Tables']['driving_log_vehicles']['Insert']
type VehicleUpdate = Database['public']['Tables']['driving_log_vehicles']['Update']
type EntryInsert = Database['public']['Tables']['driving_log_entries']['Insert']
type EntryUpdate = Database['public']['Tables']['driving_log_entries']['Update']

export const drivingLogKeys = {
  vehicles: ['drivingLogVehicles'] as const,
  entries: (vehicleId: string) => ['drivingLogEntries', vehicleId] as const,
  entriesPeriod: (vehicleId: string, from: string, to: string) =>
    ['drivingLogEntries', vehicleId, from, to] as const,
  lastEntry: (vehicleId: string) => ['drivingLogLastEntry', vehicleId] as const,
  openTrip: (vehicleId: string) => ['drivingLogOpenTrip', vehicleId] as const,
  assignmentsForDate: (date: string) => ['drivingLogAssignments', date] as const,
}

// ============================================
// Vehicle hooks
// ============================================

export const useDrivingLogVehicles = () => {
  return useQuery({
    queryKey: drivingLogKeys.vehicles,
    queryFn: () => drivingLogVehiclesService.getAll(),
    staleTime: 5 * 60 * 1000,
  })
}

export const useCreateDrivingLogVehicle = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (vehicle: VehicleInsert) => drivingLogVehiclesService.create(vehicle),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: drivingLogKeys.vehicles })
    },
  })
}

export const useUpdateDrivingLogVehicle = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: VehicleUpdate }) =>
      drivingLogVehiclesService.update(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: drivingLogKeys.vehicles })
    },
  })
}

// ============================================
// Entry hooks
// ============================================

export const useDrivingLogEntries = (vehicleId: string, dateFrom: string, dateTo: string) => {
  return useQuery({
    queryKey: drivingLogKeys.entriesPeriod(vehicleId, dateFrom, dateTo),
    queryFn: () => drivingLogEntriesService.getByVehicle(vehicleId, dateFrom, dateTo),
    enabled: !!vehicleId && !!dateFrom && !!dateTo,
    staleTime: 2 * 60 * 1000,
  })
}

export const useOpenDrivingLogTrip = (vehicleId: string) => {
  return useQuery({
    queryKey: drivingLogKeys.openTrip(vehicleId),
    queryFn: () => drivingLogEntriesService.getOpenTrip(vehicleId),
    enabled: !!vehicleId,
    staleTime: 30 * 1000,
  })
}

export const useLastDrivingLogEntry = (vehicleId: string) => {
  return useQuery({
    queryKey: drivingLogKeys.lastEntry(vehicleId),
    queryFn: () => drivingLogEntriesService.getLastEntry(vehicleId),
    enabled: !!vehicleId,
    staleTime: 1 * 60 * 1000,
  })
}

export const useCreateDrivingLogEntry = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (entry: EntryInsert) => drivingLogEntriesService.create(entry),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['drivingLogEntries', variables.vehicle_id] })
      queryClient.invalidateQueries({ queryKey: drivingLogKeys.lastEntry(variables.vehicle_id) })
      queryClient.invalidateQueries({ queryKey: drivingLogKeys.openTrip(variables.vehicle_id) })
    },
  })
}

export const useUpdateDrivingLogEntry = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; vehicleId: string; updates: EntryUpdate }) =>
      drivingLogEntriesService.update(id, updates),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['drivingLogEntries', variables.vehicleId] })
      queryClient.invalidateQueries({ queryKey: drivingLogKeys.lastEntry(variables.vehicleId) })
      queryClient.invalidateQueries({ queryKey: drivingLogKeys.openTrip(variables.vehicleId) })
    },
  })
}

export const useDeleteDrivingLogEntry = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id }: { id: string; vehicleId: string }) =>
      drivingLogEntriesService.delete(id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['drivingLogEntries', variables.vehicleId] })
      queryClient.invalidateQueries({ queryKey: drivingLogKeys.lastEntry(variables.vehicleId) })
      queryClient.invalidateQueries({ queryKey: drivingLogKeys.openTrip(variables.vehicleId) })
    },
  })
}

// ============================================
// Assignment suggestions
// ============================================

export const useAssignmentsForDate = (date: string) => {
  return useQuery({
    queryKey: drivingLogKeys.assignmentsForDate(date),
    queryFn: () => drivingLogEntriesService.getAssignmentsForDate(date),
    enabled: !!date,
    staleTime: 5 * 60 * 1000,
  })
}
